"use client";

import {
  MessageSquare,
  Eye,
  Volume2,
  FileText,
  Check,
  Sparkles,
  Zap,
  Bot,
} from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";

import { Link } from "@/shared/ui/design-system/link.js";

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 right-0 left-0 z-50 border-b border-gray-800/50 bg-black/70 backdrop-blur-md">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <nav className="flex h-14 items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white">
              <Sparkles className="h-4 w-4 text-black" />
            </div>
            <span className="text-lg font-semibold text-white">NovioAI</span>
          </div>

          <div className="hidden items-center gap-8 md:flex">
            <a
              href="#features"
              className="text-sm text-gray-400 transition-colors hover:text-white"
            >
              <FormattedMessage id="assistant-primary-landing.header.features" />
            </a>
            <a
              href="#pricing"
              className="text-sm text-gray-400 transition-colors hover:text-white"
            >
              <FormattedMessage id="assistant-primary-landing.header.pricing" />
            </a>
            <button
              type="button"
              className="rounded-lg bg-white px-4 py-1.5 text-sm font-medium text-black transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              disabled
            >
              Скоро
            </button>
            {/* <button type="button" className="bg-white text-black px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">
              <FormattedMessage id="assistant-primary-landing.header.try-button" />
            </button> */}
          </div>

          <button
            type="button"
            aria-label="Open navigation menu"
            className="text-gray-400 transition-colors hover:text-white md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </nav>

        {isMenuOpen && (
          <div className="border-t border-gray-800/50 py-4 md:hidden">
            <div className="flex flex-col gap-3">
              <a
                href="#features"
                className="px-4 py-2 text-sm text-gray-400 transition-colors hover:text-white"
              >
                <FormattedMessage id="assistant-primary-landing.header.features" />
              </a>
              <a
                href="#pricing"
                className="px-4 py-2 text-sm text-gray-400 transition-colors hover:text-white"
              >
                <FormattedMessage id="assistant-primary-landing.header.pricing" />
              </a>
              <button
                type="button"
                className="mx-4 mt-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-black disabled:cursor-not-allowed disabled:opacity-50"
                disabled
              >
                Скоро
              </button>
              {/* <button type="button" className="bg-white text-black px-4 py-2 rounded-lg text-sm font-medium mx-4 mt-2">
                <FormattedMessage id="assistant-primary-landing.header.try-button" />
              </button> */}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-b from-gray-950 via-gray-900 to-black">
    {/* Linear-style grid background - responsive */}
    <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f1f23_1px,transparent_1px),linear-gradient(to_bottom,#1f1f23_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)] bg-[size:2rem_2rem] sm:bg-[size:4rem_4rem]" />

    {/* Subtle glow effect - responsive */}
    <div className="absolute top-0 left-1/2 h-[300px] w-[300px] -translate-x-1/2 rounded-full bg-gradient-to-b from-violet-500/20 via-violet-500/5 to-transparent blur-3xl sm:h-[600px] sm:w-[600px]" />

    <div className="relative z-10 mx-auto max-w-6xl px-3 py-8 text-center sm:px-6 sm:py-0 lg:px-8">
      <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-gray-700/50 bg-gray-800/50 px-2.5 py-1 text-xs text-gray-300 backdrop-blur-sm transition-colors hover:border-gray-600/50 sm:mb-8 sm:px-3 sm:py-1.5 sm:text-sm">
        <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-400 sm:h-2 sm:w-2" />
        <span className="hidden min-[400px]:inline">
          <FormattedMessage id="assistant-primary-landing.hero.announcement" />
        </span>
        <span className="min-[400px]:hidden">
          <FormattedMessage id="assistant-primary-landing.hero.announcement-short" />
        </span>
      </div>

      <h1 className="mb-4 text-3xl leading-tight font-semibold tracking-tight min-[400px]:text-4xl sm:mb-6 sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
        <span className="block bg-gradient-to-br from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
          <FormattedMessage id="assistant-primary-landing.hero.title-line1" />
        </span>
        <span className="block bg-gradient-to-r from-violet-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
          <FormattedMessage id="assistant-primary-landing.hero.title-line2" />
        </span>
      </h1>

      <p className="mx-auto mb-8 max-w-sm px-2 text-base leading-relaxed font-light text-gray-400 sm:mb-12 sm:max-w-2xl sm:px-0 sm:text-lg lg:max-w-3xl lg:text-xl">
        <FormattedMessage id="assistant-primary-landing.hero.description" />
        <span className="hidden sm:inline">
          <FormattedMessage id="assistant-primary-landing.hero.description-extended" />
        </span>
      </p>

      <div className="mb-12 flex flex-col justify-center gap-3 px-4 sm:mb-20 sm:flex-row sm:gap-4 sm:px-0">
        <button
          type="button"
          className="group relative flex items-center justify-center gap-2 rounded-lg bg-white px-6 py-2.5 text-sm font-medium text-black transition-[background-color,opacity,scale] hover:scale-[1.02] hover:bg-gray-100 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:px-8 sm:py-3 sm:text-base"
          disabled
        >
          Скоро
        </button>
        {/* <button type="button" className="group relative bg-white text-black px-6 py-2.5 sm:px-8 sm:py-3 rounded-lg font-medium transition-all hover:bg-gray-100 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 text-sm sm:text-base">
            <span className="hidden min-[400px]:inline">
              <FormattedMessage id="assistant-primary-landing.hero.cta-primary" />
            </span>
            <span className="min-[400px]:hidden">
              <FormattedMessage id="assistant-primary-landing.hero.cta-primary-short" />
            </span>
            <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:translate-x-0.5" />
          </button> */}
        {/* <button type="button" className="group px-6 py-2.5 sm:px-8 sm:py-3 rounded-lg font-medium text-gray-300 border border-gray-700/50 hover:border-gray-600 hover:bg-gray-800/30 transition-all flex items-center justify-center gap-2 text-sm sm:text-base">
            <span className="hidden min-[400px]:inline">
              <FormattedMessage id="assistant-primary-landing.hero.cta-secondary" />
            </span>
            <span className="min-[400px]:hidden">
              <FormattedMessage id="assistant-primary-landing.hero.cta-secondary-short" />
            </span>
            <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-gray-600 flex items-center justify-center">
              <div className="w-0 h-0 border-l-[5px] sm:border-l-[6px] border-l-white border-y-[3px] sm:border-y-[4px] border-y-transparent ml-0.5"></div>
            </div>
          </button> */}
      </div>

      {/* Planned: hero stats grid */}
      {/* Linear-style stats grid - responsive */}
      {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-xs sm:max-w-2xl lg:max-w-4xl mx-auto px-2 sm:px-0">
          {[
            {
              value: "1M+",
              labelId: "assistant-primary-landing.hero.stats.requests",
            },
            {
              value: "99.9%",
              labelId: "assistant-primary-landing.hero.stats.uptime",
            },
            {
              value: "<100ms",
              labelId: "assistant-primary-landing.hero.stats.response",
            },
            {
              value: "24/7",
              labelId: "assistant-primary-landing.hero.stats.support",
            },
          ].map((stat, index) => (
            <div key={index} className="text-center group">
              <div className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white mb-0.5 sm:mb-1 group-hover:text-violet-400 transition-colors">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-gray-500 font-medium">
                <FormattedMessage id={stat.labelId} />
              </div>
            </div>
          ))}
        </div> */}
    </div>
  </section>
);

// Features Section
const FeaturesSection = () => {
  const features = [
    {
      descriptionId:
        "assistant-primary-landing.features.intelligent-chat.description",
      icon: <MessageSquare className="h-5 w-5" />,
      titleId: "assistant-primary-landing.features.intelligent-chat.title",
    },
    {
      descriptionId:
        "assistant-primary-landing.features.screen-recognition.description",
      icon: <Eye className="h-5 w-5" />,
      titleId: "assistant-primary-landing.features.screen-recognition.title",
    },
    {
      descriptionId:
        "assistant-primary-landing.features.audio-analysis.description",
      icon: <Volume2 className="h-5 w-5" />,
      titleId: "assistant-primary-landing.features.audio-analysis.title",
    },
    {
      descriptionId:
        "assistant-primary-landing.features.document-work.description",
      icon: <FileText className="h-5 w-5" />,
      titleId: "assistant-primary-landing.features.document-work.title",
    },
    {
      descriptionId:
        "assistant-primary-landing.features.multi-provider.description",
      icon: <Bot className="h-5 w-5" />,
      titleId: "assistant-primary-landing.features.multi-provider.title",
    },
    {
      descriptionId: "assistant-primary-landing.features.speed.description",
      icon: <Zap className="h-5 w-5" />,
      titleId: "assistant-primary-landing.features.speed.title",
    },
  ];

  return (
    <section id="features" className="relative bg-black py-32">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-20 text-center">
          <h2 className="mb-4 bg-gradient-to-br from-white to-gray-400 bg-clip-text text-5xl font-semibold tracking-tight text-transparent md:text-6xl">
            <FormattedMessage id="assistant-primary-landing.features.title" />
          </h2>
          <p className="mx-auto max-w-2xl text-lg font-light text-gray-400">
            <FormattedMessage id="assistant-primary-landing.features.subtitle" />
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.titleId}
              className="group relative rounded-xl border border-gray-800/50 bg-gray-900/50 p-6 backdrop-blur-sm transition-colors duration-200 hover:border-gray-700/50 hover:bg-gray-900/70"
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-800 text-gray-400 transition-colors group-hover:bg-gray-700 group-hover:text-white">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-white">
                  <FormattedMessage id={feature.titleId} />
                </h3>
              </div>
              <p className="text-sm leading-relaxed font-light text-gray-400">
                <FormattedMessage id={feature.descriptionId} />
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const plans = [
    {
      buttonTextId: "assistant-primary-landing.pricing.free.button",
      descriptionId: "assistant-primary-landing.pricing.free.description",
      featureIds: [
        "assistant-primary-landing.pricing.free.feature1",
        "assistant-primary-landing.pricing.free.feature2",
        "assistant-primary-landing.pricing.free.feature3",
        "assistant-primary-landing.pricing.free.feature4",
      ],
      nameId: "assistant-primary-landing.pricing.free.name",
      periodId: "assistant-primary-landing.pricing.free.period",
      popular: false,
      price: "0",
    },
    {
      buttonTextId: "assistant-primary-landing.pricing.pro.button",
      descriptionId: "assistant-primary-landing.pricing.pro.description",
      featureIds: [
        "assistant-primary-landing.pricing.pro.feature1",
        "assistant-primary-landing.pricing.pro.feature2",
        "assistant-primary-landing.pricing.pro.feature3",
        "assistant-primary-landing.pricing.pro.feature4",
        "assistant-primary-landing.pricing.pro.feature5",
        "assistant-primary-landing.pricing.pro.feature6",
      ],
      nameId: "assistant-primary-landing.pricing.pro.name",
      periodId: "assistant-primary-landing.pricing.pro.period",
      popular: true,
      price: "799",
    },
    {
      buttonTextId: "assistant-primary-landing.pricing.business.button",
      descriptionId: "assistant-primary-landing.pricing.business.description",
      featureIds: [
        "assistant-primary-landing.pricing.business.feature1",
        "assistant-primary-landing.pricing.business.feature2",
        "assistant-primary-landing.pricing.business.feature3",
        "assistant-primary-landing.pricing.business.feature4",
        "assistant-primary-landing.pricing.business.feature5",
        "assistant-primary-landing.pricing.business.feature6",
      ],
      nameId: "assistant-primary-landing.pricing.business.name",
      period: "",
      popular: false,
      priceId: "assistant-primary-landing.pricing.business.price",
    },
  ];

  return (
    <section id="pricing" className="bg-gray-950 py-32">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <h2 className="mb-4 bg-gradient-to-br from-white to-gray-400 bg-clip-text text-5xl font-semibold tracking-tight text-transparent md:text-6xl">
            <FormattedMessage id="assistant-primary-landing.pricing.title" />
          </h2>
          <p className="mx-auto max-w-2xl text-lg font-light text-gray-400">
            <FormattedMessage id="assistant-primary-landing.pricing.subtitle" />
          </p>
        </div>

        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.nameId}
              className={`relative rounded-2xl border p-8 transition-colors duration-200 ${
                plan.popular
                  ? "border-gray-700 bg-gray-900/80 ring-1 ring-violet-500/20"
                  : "border-gray-800 bg-gray-900/50 hover:border-gray-700"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
                  <span className="rounded-full bg-white px-4 py-1 text-xs font-medium text-black">
                    <FormattedMessage id="assistant-primary-landing.pricing.popular" />
                  </span>
                </div>
              )}

              <div className="mb-8">
                <h3 className="mb-2 text-xl font-semibold text-white">
                  <FormattedMessage id={plan.nameId} />
                </h3>
                <div className="mb-3">
                  {plan.priceId ? (
                    <span className="text-3xl font-semibold text-white">
                      <FormattedMessage id={plan.priceId} />
                    </span>
                  ) : (
                    <div className="flex items-baseline">
                      <span className="text-4xl font-semibold text-white">
                        {plan.price}
                      </span>
                      <span className="ml-1 text-lg text-gray-400">₽</span>
                      {plan.periodId && (
                        <span className="ml-2 text-sm text-gray-500">
                          <FormattedMessage id={plan.periodId} />
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-400">
                  <FormattedMessage id={plan.descriptionId} />
                </p>
              </div>

              <ul className="mb-8 space-y-3">
                {plan.featureIds.map((featureId, featureIndex) => (
                  <li
                    key={featureIndex}
                    className="flex items-center text-sm text-gray-300"
                  >
                    <Check className="mr-3 h-4 w-4 flex-shrink-0 text-gray-400" />
                    <FormattedMessage id={featureId} />
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className={`w-full rounded-lg py-3 font-medium transition-all ${
                  plan.popular
                    ? "bg-white text-black hover:bg-gray-100 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                    : "border border-gray-700 text-gray-300 hover:border-gray-600 hover:bg-gray-800/50 disabled:cursor-not-allowed disabled:opacity-50"
                }`}
                disabled
              >
                Скоро
              </button>
              {/* <button type="button"
                className={`w-full py-3 rounded-lg font-medium transition-all ${
                  plan.popular
                    ? "bg-white text-black hover:bg-gray-100 active:scale-[0.98]"
                    : "border border-gray-700 text-gray-300 hover:border-gray-600 hover:bg-gray-800/50"
                }`}
              >
                <FormattedMessage id={plan.buttonTextId} />
              </button> */}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="bg-black py-32">
    <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
      <h2 className="mb-6 bg-gradient-to-br from-white to-gray-400 bg-clip-text text-5xl font-semibold tracking-tight text-transparent md:text-6xl">
        <FormattedMessage id="assistant-primary-landing.cta.title" />
      </h2>
      <p className="mx-auto mb-12 max-w-2xl text-lg font-light text-gray-400">
        <FormattedMessage id="assistant-primary-landing.cta.description" />
      </p>

      <div className="flex flex-col justify-center gap-4 sm:flex-row">
        <button
          type="button"
          className="group flex items-center justify-center gap-2 rounded-lg bg-white px-8 py-3 font-medium text-black transition-[background-color,opacity,scale] hover:scale-[1.02] hover:bg-gray-100 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          disabled
        >
          Скоро
        </button>
        {/* <button type="button" className="group bg-white text-black px-8 py-3 rounded-lg font-medium transition-all hover:bg-gray-100 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2">
            <FormattedMessage id="assistant-primary-landing.cta.primary" />
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button> */}
        {/* <button type="button" className="px-8 py-3 rounded-lg font-medium text-gray-300 border border-gray-700 hover:border-gray-600 hover:bg-gray-800/30 transition-all">
            <FormattedMessage id="assistant-primary-landing.cta.secondary" />
          </button> */}
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="border-t border-gray-800/50 bg-black py-16">
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white">
              <Sparkles className="h-4 w-4 text-black" />
            </div>
            <span className="text-lg font-semibold text-white">NovioAI</span>
          </div>
          <p className="max-w-md text-sm text-gray-400">
            <FormattedMessage id="assistant-primary-landing.footer.description" />
          </p>
        </div>

        <div>
          <h3 className="mb-4 text-sm font-medium text-white">
            <FormattedMessage id="assistant-primary-landing.footer.product.title" />
          </h3>
          <ul className="space-y-3 text-sm text-gray-400">
            <li>
              <a
                href="#features"
                className="transition-colors hover:text-white"
              >
                <FormattedMessage id="assistant-primary-landing.footer.product.features" />
              </a>
            </li>
            <li>
              <a href="#pricing" className="transition-colors hover:text-white">
                <FormattedMessage id="assistant-primary-landing.footer.product.pricing" />
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="mb-4 text-sm font-medium text-white">Контакты</h3>
          <ul className="space-y-3 text-sm text-gray-400">
            <li>
              <a
                href="mailto:support@novioai.com"
                className="transition-colors hover:text-white"
              >
                support@novioai.com
              </a>
            </li>
            <li>
              <a
                href="https://t.me/novioai"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-white"
              >
                Telegram
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-gray-800/50 pt-8 md:flex-row">
        <div className="text-center text-xs text-gray-500 md:text-left">
          <p className="mb-1">
            <FormattedMessage id="assistant-primary-landing.footer.copyright" />
          </p>
        </div>
        <div className="flex gap-6 text-xs">
          <Link
            href="/privacy"
            className="text-gray-500 transition-colors hover:text-gray-300"
          >
            <FormattedMessage id="assistant-primary-landing.footer.privacy" />
          </Link>
          <Link
            href="/terms"
            className="text-gray-500 transition-colors hover:text-gray-300"
          >
            <FormattedMessage id="assistant-primary-landing.footer.terms" />
          </Link>
        </div>
      </div>
    </div>
  </footer>
);

const LandingPage = () => (
  <div className="min-h-screen overflow-x-hidden bg-black text-white">
    <Header />
    <HeroSection />
    <FeaturesSection />
    <PricingSection />
    {/* WIP: testimonials section */}
    <CTASection />
    <Footer />
  </div>
);

export default LandingPage;
